import { ArrowRight, ClipboardCheck, FileOutput, Plus, RefreshCw, RotateCcw, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import {
  api,
  type VnnicGenerateReportRequest,
  type VnnicItemError,
  type VnnicReportBatch,
  type VnnicReportBatchDetail,
  type VnnicReportOutcomeRequest,
  type VnnicReportType,
} from '../../../api';
import { Badge } from '../../../components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, type Column } from '../../../components/blocks';
import { EntityRef } from '../../../components/EntityRef';
import { KeyValue } from '../../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../../components/PageHeader';
import { CopyText, EmptyState, ErrorState, Loading } from '../../../components/States';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Textarea } from '../../../components/ui/textarea';
import { formatCount, formatDateTime } from '../../../lib/format';
import { cn } from '../../../lib/utils';
import { useApi } from '../../../lib/useApi';
import { ActionDialog, EnumBadge, FormActions, FormGrid, SelectField, SuccessNote, useEnumLabel } from '../ui';
import {
  EvidenceHelp,
  Payload,
  periodLabel,
  ReceiptsTable,
  REPORT_STATUS,
  SchemaBadge,
  UnofficialSchemaNotice,
  useRetained,
  useWrite,
  VNNIC_BASE,
  WriteError,
} from './shared';

const LIST_LIMIT = 100;
const LIST_PATH = `${VNNIC_BASE}/international-reports`;
const batchPath = (id: string) => `${LIST_PATH}/${encodeURIComponent(id)}`;
const REPORT_TYPES: readonly VnnicReportType[] = ['0805A', '0805B'];
const QUARTERS = ['1', '2', '3', '4'] as const;
type Quarter = 1 | 2 | 3 | 4;

function isReportType(value: string | null): value is VnnicReportType {
  return value === '0805A' || value === '0805B';
}

/** The most recent closed period in Vietnam (UTC+7): last year, and last quarter. */
function lastClosedPeriod(now: number = Date.now()): {
  year: number;
  quarterYear: number;
  quarter: Quarter;
} {
  const vn = new Date(now + 7 * 3_600_000);
  const year = vn.getUTCFullYear();
  const q = Math.floor(vn.getUTCMonth() / 3); // current quarter index 0..3
  return q === 0 ? { year: year - 1, quarterYear: year - 1, quarter: 4 } : { year: year - 1, quarterYear: year, quarter: q as Quarter };
}

function useReportTypeOptions() {
  const { t } = useTranslation('compliance');
  return REPORT_TYPES.map((value) => ({
    value,
    label: t(`vnnic.reportType.${value}`),
  }));
}

// ---------------------------------------------------------------------------
// Generate

function GenerateForm({ onGenerated }: { onGenerated: (id: string, message: string) => void }) {
  const { t } = useTranslation('compliance');
  const label = useEnumLabel();
  const typeOptions = useReportTypeOptions();
  const [defaults] = useState(() => lastClosedPeriod());
  const [type, setType] = useState<VnnicReportType>('0805B');
  const [year, setYear] = useState(String(defaults.quarterYear));
  const [quarter, setQuarter] = useState<Quarter>(defaults.quarter);
  const write = useWrite((req: VnnicGenerateReportRequest, opts) => api.generateVnnicReport(req, opts));

  const yearNumber = Number(year);
  const validYear = /^\d{4}$/.test(year.trim()) && yearNumber >= 2000 && yearNumber <= 2100;

  const changeType = (next: VnnicReportType) => {
    setType(next);
    setYear(String(next === '0805A' ? defaults.year : defaults.quarterYear));
    write.reset();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!validYear) return;
    const req: VnnicGenerateReportRequest = type === '0805A' ? { report_code: '0805A', year: yearNumber } : { report_code: '0805B', year: yearNumber, quarter };
    const res = await write.run(req);
    if (res) {
      const period = type === '0805A' ? String(yearNumber) : `${yearNumber} Q${quarter}`;
      onGenerated(
        res.id,
        t('vnnic.reports.generate.done', {
          type: res.report_type,
          period,
          revision: res.revision,
          status: label('reportStatus', res.status).toLowerCase(),
          records: formatCount(res.record_count),
          invalid: formatCount(res.invalid_count),
        }),
      );
    }
  };

  return (
    <Section title={t('vnnic.reports.generate.title')} description={t('vnnic.reports.generate.hint')}>
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <SelectField
          label={t('vnnic.reports.generate.report')}
          value={type}
          options={typeOptions}
          onChange={(v) => v && changeType(v)}
          disabled={write.busy}
          className="w-full sm:w-52"
        />
        <Field label={t('vnnic.reports.generate.year')} className="w-28">
          <Input
            type="number"
            inputMode="numeric"
            min={2000}
            max={2100}
            value={year}
            onChange={(e) => setYear(e.target.value)}
            disabled={write.busy}
            required
            aria-invalid={!validYear}
          />
        </Field>
        {type === '0805B' ? (
          <SelectField
            label={t('vnnic.reports.generate.quarter')}
            value={String(quarter) as (typeof QUARTERS)[number]}
            options={QUARTERS.map((q) => ({ value: q, label: `Q${q}` }))}
            onChange={(v) => v && setQuarter(Number(v) as Quarter)}
            disabled={write.busy}
            className="w-28 min-w-0"
          />
        ) : null}
        <Button type="submit" disabled={write.busy || !validYear}>
          <Plus /> {write.busy ? t('vnnic.reports.generate.busy') : t('vnnic.reports.generate.submit')}
        </Button>
      </form>
      {write.state.phase === 'error' ? (
        <div className="mt-3">
          <WriteError error={write.state.error} />
        </div>
      ) : null}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Batch actions

type OutcomeKind = VnnicReportOutcomeRequest['outcome'];

function allowedOutcomes(batch: VnnicReportBatch): OutcomeKind[] {
  if (batch.status === 'EXPORTED') return ['SUBMITTED'];
  if (batch.status === 'SUBMITTED') return ['ACKNOWLEDGED', 'REJECTED'];
  return [];
}

interface ItemErrorDraft {
  key: number;
  line: string;
  code: string;
  message: string;
}

function ReportOutcomeForm({ batch, onDone, onCancel }: { batch: VnnicReportBatchDetail; onDone: (message: string) => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const options = allowedOutcomes(batch);
  const [outcome, setOutcome] = useState<OutcomeKind>(options[0] ?? 'SUBMITTED');
  const [reference, setReference] = useState('');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [evidence, setEvidence] = useState('');
  const [note, setNote] = useState('');
  const [itemErrors, setItemErrors] = useState<ItemErrorDraft[]>([]);
  const nextKey = useRef(0);
  const write = useWrite((req: VnnicReportOutcomeRequest, opts) => api.recordVnnicReportOutcome(batch.id, req, opts));

  const lines = new Set(batch.items.map((i) => i.line_number));
  const parsedErrors: (VnnicItemError | null)[] = itemErrors.map((e) => {
    const line = Number(e.line);
    return Number.isInteger(line) && lines.has(line) && e.code.trim() && e.message.trim() ? { line_number: line, code: e.code.trim(), message: e.message.trim() } : null;
  });
  const validErrors = parsedErrors.flatMap((e) => (e ? [e] : []));
  const itemErrorsValid = validErrors.length === parsedErrors.length;
  const complete =
    evidence.trim() !== '' &&
    (outcome === 'SUBMITTED' ||
      (outcome === 'ACKNOWLEDGED' && reference.trim() !== '') ||
      (outcome === 'REJECTED' && code.trim() !== '' && message.trim() !== '' && itemErrorsValid));

  const updateItemError = (key: number, patch: Partial<ItemErrorDraft>) => setItemErrors((list) => list.map((e) => (e.key === key ? { ...e, ...patch } : e)));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!complete) return;
    const base = {
      evidence_object_key: evidence.trim(),
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    let req: VnnicReportOutcomeRequest;
    if (outcome === 'SUBMITTED') req = { ...base, outcome, reference: reference.trim() || null };
    else if (outcome === 'ACKNOWLEDGED') req = { ...base, outcome, reference: reference.trim() };
    else
      req = {
        ...base,
        outcome,
        code: code.trim(),
        message: message.trim(),
        item_errors: validErrors,
      };
    const res = await write.run(req);
    if (res)
      onDone(
        t('vnnic.reports.outcome.done', {
          outcome: t(`vnnic.reports.outcome.label.${outcome}`),
          status: label('reportStatus', res.status).toLowerCase(),
        }),
      );
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      <FormGrid>
        <SelectField
          label={t('vnnic.reports.outcome.outcome')}
          value={outcome}
          options={options.map((o) => ({
            value: o,
            label: t(`vnnic.reports.outcome.label.${o}`),
          }))}
          onChange={(v) => v && setOutcome(v)}
          disabled={write.busy || options.length < 2}
        />
        {outcome !== 'REJECTED' ? (
          <Field label={outcome === 'ACKNOWLEDGED' ? t('vnnic.reports.outcome.referenceRequired') : t('vnnic.reports.outcome.referenceOptional')}>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} disabled={write.busy} autoComplete="off" required={outcome === 'ACKNOWLEDGED'} />
          </Field>
        ) : (
          <>
            <Field label={t('vnnic.reports.outcome.code')}>
              <Input value={code} onChange={(e) => setCode(e.target.value)} disabled={write.busy} autoComplete="off" required />
            </Field>
            <Field label={t('vnnic.reports.outcome.message')} className="sm:col-span-2">
              <Input value={message} onChange={(e) => setMessage(e.target.value)} disabled={write.busy} autoComplete="off" required />
            </Field>
          </>
        )}
        <Field label={t('vnnic.reports.outcome.evidence')} className="sm:col-span-2" hint={<EvidenceHelp />}>
          <Input
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            disabled={write.busy}
            autoComplete="off"
            placeholder="evidence/vnnic/…"
            required
            className="font-mono"
          />
        </Field>
        <Field label={t('vnnic.reports.outcome.note')} className="sm:col-span-2">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} disabled={write.busy} rows={2} maxLength={1000} />
        </Field>
      </FormGrid>

      {outcome === 'REJECTED' ? (
        <fieldset className="grid gap-3 rounded-md border p-3" disabled={write.busy}>
          <legend className="px-1 text-sm font-semibold">{t('vnnic.reports.outcome.itemErrors')}</legend>
          <p className="text-xs text-muted-foreground">{t('vnnic.reports.outcome.itemErrorsHint')}</p>
          {itemErrors.map((e, i) => {
            const invalid = !parsedErrors[i] && Boolean(e.line || e.code || e.message);
            return (
              <div key={e.key} className={cn('grid gap-2 rounded-md p-2 sm:grid-cols-[5rem_8rem_1fr_auto] sm:items-end', invalid && 'bg-critical-soft ring-1 ring-critical/30')}>
                <Field label={t('vnnic.reports.outcome.line')}>
                  <Input type="number" min={1} value={e.line} onChange={(ev) => updateItemError(e.key, { line: ev.target.value })} />
                </Field>
                <Field label={t('vnnic.reports.outcome.code_')}>
                  <Input value={e.code} onChange={(ev) => updateItemError(e.key, { code: ev.target.value })} autoComplete="off" />
                </Field>
                <Field label={t('vnnic.reports.outcome.message_')}>
                  <Input value={e.message} onChange={(ev) => updateItemError(e.key, { message: ev.target.value })} autoComplete="off" />
                </Field>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('vnnic.reports.outcome.removeItem', {
                    n: i + 1,
                  })}
                  onClick={() => setItemErrors((list) => list.filter((x) => x.key !== e.key))}
                >
                  <Trash2 />
                </Button>
              </div>
            );
          })}
          <div>
            <Button type="button" variant="outline" size="sm" onClick={() => setItemErrors((list) => [...list, { key: nextKey.current++, line: '', code: '', message: '' }])}>
              <Plus /> {t('vnnic.reports.outcome.addItem')}
            </Button>
          </div>
        </fieldset>
      ) : null}

      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !complete}>
          {write.busy ? t('vnnic.reports.outcome.busy') : t('vnnic.reports.outcome.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

function BatchActions({ batch, onChanged }: { batch: VnnicReportBatchDetail; onChanged: (message: string) => void }) {
  const { t } = useTranslation('compliance');
  const label = useEnumLabel();
  const [recording, setRecording] = useState(false);
  const exporter = useWrite((_: null, opts) => api.exportVnnicReport(batch.id, opts));
  const canExport = batch.status === 'VALIDATED';
  const outcomes = allowedOutcomes(batch);

  const runExport = async () => {
    const res = await exporter.run(null);
    if (res)
      onChanged(
        t('vnnic.reports.actions.exported', {
          adapter: res.adapter,
          status: label('reportStatus', res.status).toLowerCase(),
        }),
      );
  };

  if (!canExport && outcomes.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('vnnic.reports.actions.none', {
          status: label('reportStatus', batch.status),
        })}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {canExport ? (
          <Button onClick={runExport} disabled={exporter.busy}>
            <FileOutput /> {exporter.busy ? t('vnnic.reports.actions.exporting') : t('vnnic.reports.actions.export')}
          </Button>
        ) : null}
        {outcomes.length ? (
          <Button onClick={() => setRecording(true)}>
            <ClipboardCheck /> {t('vnnic.reports.actions.record')}
          </Button>
        ) : null}
      </div>
      {exporter.state.phase === 'error' ? <WriteError error={exporter.state.error} /> : null}
      <ActionDialog open={recording} onOpenChange={setRecording} title={t('vnnic.reports.outcome.title')} wide>
        <ReportOutcomeForm
          batch={batch}
          onCancel={() => setRecording(false)}
          onDone={(message) => {
            setRecording(false);
            onChanged(message);
          }}
        />
      </ActionDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Batch detail

function RevisionChain({ batch, siblings, onSelect }: { batch: VnnicReportBatch; siblings: VnnicReportBatch[]; onSelect: (id: string) => void }) {
  const { t } = useTranslation('compliance');
  const chain = [...siblings].sort((a, b) => a.revision - b.revision);
  if (!chain.some((b) => b.id === batch.id)) chain.push(batch);
  return (
    <ol className="flex flex-wrap items-center gap-2" aria-label={t('vnnic.reports.batch.chainLabel')}>
      {chain.map((b, i) => (
        <li key={b.id} className="flex items-center gap-2">
          {i > 0 ? <ArrowRight className="size-3.5 text-muted-foreground" aria-hidden="true" /> : null}
          {b.id === batch.id ? (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-primary bg-accent px-2 py-1 text-sm font-medium" aria-current="true">
              r{b.revision} <EnumBadge group="reportStatus" value={b.status} tones={REPORT_STATUS} />
            </span>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={() => onSelect(b.id)}>
              r{b.revision} <EnumBadge group="reportStatus" value={b.status} tones={REPORT_STATUS} />
            </Button>
          )}
        </li>
      ))}
    </ol>
  );
}

type Item = VnnicReportBatchDetail['items'][number];

function BatchDetail({
  id,
  version,
  list,
  onSelect,
  onClose,
  onChanged,
}: {
  id: string;
  version: number;
  list: VnnicReportBatch[];
  onSelect: (id: string) => void;
  onClose: () => void;
  onChanged: (message: string) => void;
}) {
  const { t } = useTranslation('compliance');
  const result = useApi((signal) => api.getVnnicReport(id, { signal }), [id, version]);
  const batch = useRetained(result.status === 'success' ? result.data : undefined);
  const heading = useRef<HTMLHeadingElement>(null);
  const hasBatch = batch !== undefined;
  useEffect(() => {
    if (hasBatch) heading.current?.focus();
  }, [hasBatch]);

  if (!batch) {
    return (
      <Section>
        {result.status === 'error' ? (
          <ErrorState error={result.error} onRetry={result.reload} title={t('vnnic.reports.batch.loadError')} />
        ) : (
          <Loading label={t('vnnic.reports.batch.loading')} />
        )}
      </Section>
    );
  }

  const siblings = list.filter((b) => b.report_type === batch.report_type && b.period_start === batch.period_start);
  const supersededBy = list.find((b) => b.supersedes_batch_id === batch.id);
  const supersedes = batch.supersedes_batch_id ? list.find((b) => b.id === batch.supersedes_batch_id) : undefined;
  const invalidItems = batch.items.filter((i) => i.validation_status === 'INVALID').length;

  const itemCols: Column<Item>[] = [
    {
      key: 'line',
      header: t('vnnic.reports.batch.col.line'),
      numeric: true,
      cell: (item) => item.line_number,
    },
    {
      key: 'domain',
      header: t('vnnic.reports.batch.col.domain'),
      cell: (item) => (
        <EntityRef label={item.unicode_name || item.domain_name} to={`/domains/${encodeURIComponent(item.domain_name)}`} id={item.domain_id} />
      ),
    },
    {
      key: 'change',
      header: t('vnnic.reports.batch.col.change'),
      cell: (item) => <code className="text-xs">{item.change_type}</code>,
    },
    {
      key: 'validation',
      header: t('vnnic.reports.batch.col.validation'),
      cell: (item) => (
        <>
          {item.validation_status === 'INVALID' ? <Badge tone="crit">{t('vnnic.reports.batch.invalid')}</Badge> : <Badge tone="ok">{t('vnnic.reports.batch.valid')}</Badge>}
          {item.validation_errors.length ? (
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-critical">
              {item.validation_errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          ) : null}
        </>
      ),
    },
    {
      key: 'submission',
      header: t('vnnic.reports.batch.col.submission'),
      cell: (item) => (
        <>
          {item.submission_status ? <code className="text-xs">{item.submission_status}</code> : <span className="text-muted-foreground">—</span>}
          {item.error_code !== null || item.error_message !== null ? (
            <div className="mt-1 text-xs text-critical">
              {item.error_code ? <code>{item.error_code}</code> : null} {item.error_message}
            </div>
          ) : null}
        </>
      ),
    },
    {
      key: 'payload',
      header: t('vnnic.reports.batch.col.payload'),
      cell: (item) => <Payload label={t('vnnic.reports.batch.line', { line: item.line_number })} value={item.payload} />,
    },
  ];

  return (
    <Section
      title={
        <div className="space-y-2">
          <h2 id="batch-detail-title" ref={heading} tabIndex={-1} className="outline-none">
            {t('vnnic.reports.batch.title', {
              type: t(`vnnic.reportType.${batch.report_type}`),
              period: periodLabel(batch.report_type, batch.period_start),
              revision: batch.revision,
            })}
          </h2>
          <div className="flex flex-wrap gap-1.5">
            <EnumBadge group="reportStatus" value={batch.status} tones={REPORT_STATUS} />
            <SchemaBadge status={batch.schema_status} version={batch.schema_version} />
          </div>
        </div>
      }
      actions={
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('vnnic.reports.batch.close')}>
          <X />
        </Button>
      }
    >
      <div className="space-y-4">
        {batch.schema_status === 'UNOFFICIAL_DRAFT' ? (
          <Callout tone="warn">
            {t('vnnic.reports.batch.unofficial', {
              version: batch.schema_version,
            })}
          </Callout>
        ) : null}
        {result.status === 'error' ? <ErrorState error={result.error} onRetry={result.reload} title={t('vnnic.reports.batch.refreshError')} /> : null}

        <Tabs defaultValue="summary">
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="summary">{t('vnnic.reports.batch.tabs.summary')}</TabsTrigger>
            <TabsTrigger value="items">
              {t('vnnic.reports.batch.tabs.items')}
              {invalidItems > 0 ? (
                <Badge tone="crit" className="ml-1">
                  {t('vnnic.reports.batch.invalidItems', {
                    count: invalidItems,
                  })}
                </Badge>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="actions">{t('vnnic.reports.batch.tabs.actions')}</TabsTrigger>
            <TabsTrigger value="receipts">{t('vnnic.reports.batch.tabs.receipts')}</TabsTrigger>
          </TabsList>
          <TabsContent value="summary" className="mt-3 space-y-5">
            <KeyValue
              columns={3}
              rows={[
                [t('vnnic.reports.batch.id'), <CopyText key="id" value={batch.id} />],
                [t('vnnic.reports.batch.period'), `${formatDateTime(batch.period_start)} → ${formatDateTime(batch.period_end)}`],
                [
                  t('vnnic.reports.batch.records'),
                  t('vnnic.reports.batch.recordsValue', {
                    records: formatCount(batch.record_count),
                    invalid: formatCount(batch.invalid_count),
                  }),
                ],
                [
                  t('vnnic.reports.batch.checksum'),
                  <span key="c" className="flex flex-wrap items-center gap-1.5">
                    {batch.checksum_verified ? (
                      <Badge tone="ok">{t('vnnic.reports.batch.verified')}</Badge>
                    ) : (
                      <Badge tone="crit" title={t('vnnic.reports.batch.mismatchHint')}>
                        {t('vnnic.reports.batch.mismatch')}
                      </Badge>
                    )}
                    {batch.checksum ? <CopyText value={batch.checksum} display={`${batch.checksum.slice(0, 23)}…`} /> : <span className="text-muted-foreground">—</span>}
                  </span>,
                ],
                [t('vnnic.reports.batch.schema'), <code key="s">{batch.schema_version}</code>],
                [
                  t('vnnic.reports.batch.generated'),
                  batch.generated_by_display_name
                    ? t('vnnic.reports.batch.generatedBy', {
                        at: formatDateTime(batch.generated_at),
                        who: batch.generated_by_display_name,
                      })
                    : formatDateTime(batch.generated_at),
                ],
                [t('vnnic.reports.batch.exported'), formatDateTime(batch.exported_at)],
                [
                  t('vnnic.reports.batch.exportObject'),
                  batch.export_object_key ? (
                    <code key="x" className="break-all">
                      {batch.export_object_key}
                    </code>
                  ) : null,
                ],
                [t('vnnic.reports.batch.submitted'), formatDateTime(batch.submitted_at)],
                [t('vnnic.reports.batch.acknowledged'), formatDateTime(batch.acknowledged_at)],
                [t('vnnic.reports.batch.reference'), batch.external_reference ? <code key="r">{batch.external_reference}</code> : null],
                [
                  t('vnnic.reports.batch.supersedes'),
                  batch.supersedes_batch_id ? (
                    supersedes ? (
                      <Button key="sp" variant="link" className="h-auto p-0" onClick={() => onSelect(supersedes.id)}>
                        {t('vnnic.reports.batch.revision', {
                          revision: supersedes.revision,
                        })}
                      </Button>
                    ) : (
                      <Button key="sp" variant="link" className="h-auto p-0" onClick={() => onSelect(batch.supersedes_batch_id ?? '')}>
                        {t('vnnic.reports.batch.supersedes')}
                      </Button>
                    )
                  ) : (
                    t('vnnic.reports.batch.firstRevision')
                  ),
                ],
                supersededBy
                  ? [
                      t('vnnic.reports.batch.supersededBy'),
                      <Button key="sb" variant="link" className="h-auto p-0" onClick={() => onSelect(supersededBy.id)}>
                        {t('vnnic.reports.batch.revision', {
                          revision: supersededBy.revision,
                        })}
                      </Button>,
                    ]
                  : false,
              ]}
            />
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">{t('vnnic.reports.batch.chain')}</h3>
              <RevisionChain batch={batch} siblings={siblings} onSelect={onSelect} />
            </div>
          </TabsContent>
          <TabsContent value="items" className="mt-3">
            {batch.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('vnnic.reports.batch.noItems')}</p>
            ) : (
              <div className="rounded-md border">
                <DataTable columns={itemCols} rows={batch.items} rowKey={(item) => String(item.line_number)} />
              </div>
            )}
          </TabsContent>
          <TabsContent value="actions" className="mt-3">
            <BatchActions key={`${batch.id}-${batch.status}`} batch={batch} onChanged={onChanged} />
          </TabsContent>
          <TabsContent value="receipts" className="mt-3">
            <ReceiptsTable receipts={batch.receipts} statusMap={REPORT_STATUS} group="reportStatus" />
          </TabsContent>
        </Tabs>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Page

export function VnnicInternationalReportsPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const typeOptions = useReportTypeOptions();
  useDocumentTitle(t('vnnic.reports.title'));
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const rawType = params.get('type');
  const type = isReportType(rawType) ? rawType : undefined;
  const [version, setVersion] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const result = useApi((signal) => api.listVnnicReports({ report_type: type, limit: LIST_LIMIT }, { signal }), [type, version]);
  const items = useRetained(result.status === 'success' ? result.data.items : undefined) ?? [];

  const update = (changes: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(changes)) {
          if (v) p.set(k, v);
          else p.delete(k);
        }
        return p;
      },
      { replace: true },
    );

  const columns: Column<VnnicReportBatch>[] = [
    {
      key: 'report',
      header: t('vnnic.reportCols.report'),
      cell: (b) => <span className="font-medium whitespace-nowrap">{t(`vnnic.reportType.${b.report_type}`)}</span>,
    },
    {
      key: 'period',
      header: t('vnnic.reportCols.period'),
      cell: (b) => <span className="whitespace-nowrap">{periodLabel(b.report_type, b.period_start)}</span>,
    },
    {
      key: 'rev',
      header: t('vnnic.reportCols.revision'),
      cell: (b) => (
        <>
          r{b.revision}
          {b.supersedes_batch_id ? (
            <span className="ml-1 text-xs text-muted-foreground" title={t('vnnic.reports.supersedes')}>
              ↺
            </span>
          ) : null}
        </>
      ),
    },
    {
      key: 'status',
      header: t('vnnic.reportCols.status'),
      cell: (b) => <EnumBadge group="reportStatus" value={b.status} tones={REPORT_STATUS} />,
    },
    {
      key: 'records',
      header: t('vnnic.reportCols.records'),
      numeric: true,
      cell: (b) => formatCount(b.record_count),
    },
    {
      key: 'invalid',
      header: t('vnnic.reportCols.invalid'),
      numeric: true,
      cell: (b) => (b.invalid_count > 0 ? <Badge tone="crit">{formatCount(b.invalid_count)}</Badge> : '0'),
    },
    {
      key: 'schema',
      header: t('vnnic.reportCols.schema'),
      cell: (b) => (
        <span title={b.schema_version}>
          <SchemaBadge status={b.schema_status} />
        </span>
      ),
    },
    {
      key: 'generated',
      header: t('vnnic.reportCols.generated'),
      cell: (b) => <span className="whitespace-nowrap">{formatDateTime(b.generated_at)}</span>,
    },
    {
      key: 'details',
      header: <span className="sr-only">{t('common.details')}</span>,
      cell: (b) => (
        <Button asChild size="sm" variant="outline">
          <Link to={batchPath(b.id)}>{t('common.details')}</Link>
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('vnnic.reports.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.vnnic') }]}
        subtitle={t('vnnic.reports.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={() => setVersion((n) => n + 1)} disabled={result.status === 'loading'}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />

      <div className="space-y-6">
        <UnofficialSchemaNotice />
        {notice ? <SuccessNote>{notice}</SuccessNote> : null}
        <GenerateForm
          onGenerated={(id, message) => {
            setNotice(message);
            navigate(batchPath(id));
          }}
        />

        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              <>
                {result.status === 'success' ? (
                  <span className="text-xs text-muted-foreground">
                    {t('vnnic.reports.count', { count: items.length })}
                    {items.length >= LIST_LIMIT ? ` · ${t('common.showingFirst', { count: LIST_LIMIT })}` : ''}
                  </span>
                ) : null}
                {type ? (
                  <Button variant="ghost" size="sm" onClick={() => update({ type: null })}>
                    <RotateCcw /> {tc('action.reset')}
                  </Button>
                ) : null}
              </>
            }
          >
            <SelectField
              label={t('vnnic.reports.filter')}
              value={type ?? ''}
              allLabel={t('vnnic.reports.allReports')}
              options={typeOptions}
              onChange={(v) => update({ type: v || null })}
              className="w-full sm:w-52"
            />
          </FilterBar>
          {result.status === 'loading' && items.length === 0 ? (
            <div className="px-5">
              <Loading label={t('vnnic.reports.loading')} rows={3} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <ErrorState error={result.error} onRetry={result.reload} title={t('vnnic.reports.loadError')} />
            </div>
          ) : null}
          {result.status !== 'error' && (items.length > 0 || result.status === 'success') ? (
            <DataTable columns={columns} rows={items} rowKey={(b) => b.id} empty={<EmptyState title={t('vnnic.reports.empty')}>{t('vnnic.reports.emptyHint')}</EmptyState>} />
          ) : null}
        </Section>
      </div>
    </>
  );
}

/**
 * `/compliance/vnnic/international-reports/:id`: one report batch, the lines it reports, the
 * answers recorded for it and the revisions that replaced it.
 */
export function VnnicReportBatchPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [version, setVersion] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  useDocumentTitle(t('vnnic.reports.title'));
  // The batch list names the other revisions of the same report, so the chain reads without ids.
  const list = useApi((signal) => api.listVnnicReports({ limit: LIST_LIMIT }, { signal }), [version]);
  const siblings = useRetained(list.status === 'success' ? list.data.items : undefined) ?? [];

  return (
    <>
      <PageHeader
        title={t('vnnic.reports.title')}
        crumbs={[
          { label: t('crumbs.compliance') },
          { label: t('crumbs.vnnic') },
          { label: t('vnnic.reports.title'), to: LIST_PATH },
          { label: t('common.details') },
        ]}
        actions={
          <Button size="sm" variant="outline" onClick={() => setVersion((n) => n + 1)}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-3">
        {notice ? <SuccessNote>{notice}</SuccessNote> : null}
        <BatchDetail
          key={id}
          id={id}
          version={version}
          list={siblings}
          onSelect={(next) => navigate(batchPath(next))}
          onClose={() => navigate(LIST_PATH)}
          onChanged={(message) => {
            setNotice(message);
            setVersion((n) => n + 1);
          }}
        />
      </div>
    </>
  );
}
